import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import BackButton from '../../components/ui/BackNavigationButton';
import {
  ChevronLeft,
  ChevronRight,
  Save,
  Check,
  Search,
  X,
  Filter,
  SortAsc,
  SortDesc,
  Users,
  FilterX
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAlert } from '../../context/alert/AlertProvider';
import { useTranslation } from 'react-i18next';

const SearchAndFilterComponent = ({
  searchQuery,
  setSearchQuery,
  sortOption,
  setSortOption,
  employeeCount,
  filteredCount,
  allEmployees,
  selectedEmployees,
  setSelectedEmployees,
  showSearchSuggestions,
  setShowSearchSuggestions,
  hasUserInteracted,
  setHasUserInteracted,
}) => {
  const { t } = useTranslation();
  const [showSortModal, setShowSortModal] = useState(false);
  const dropdownRef = useRef(null);

  const sortOptions = [
    { id: 'name_asc', label: t("schedules.filters.sortNameAZ") || "Nome A-Z", icon: SortAsc },
    { id: 'name_desc', label: t("schedules.filters.sortNameZA") || "Nome Z-A", icon: SortDesc },
    { id: 'lastname_asc', label: t("schedules.filters.sortLastNameAZ") || "Sobrenome A-Z", icon: SortAsc },
    { id: 'lastname_desc', label: t("schedules.filters.sortLastNameZA") || "Sobrenome Z-A", icon: SortDesc },
    { id: 'employee_asc', label: t("schedules.filters.sortEmployeeAsc") || "Funcionário ↑", icon: SortAsc },
    { id: 'employee_desc', label: t("schedules.filters.sortEmployeeDesc") || "Funcionário ↓", icon: SortDesc },
  ];

  const searchFilteredEmployees = useMemo(() => {
    if (!allEmployees) return [];

    if (!searchQuery.trim()) return allEmployees;

    const query = searchQuery.toLowerCase();
    return allEmployees.filter(employee => {
      const name = (employee.employee || '').toLowerCase();
      return name.includes(query);
    });
  }, [allEmployees, searchQuery]);

  const clearSearch = () => {
    setSearchQuery('');
    setShowSearchSuggestions(false);
  };

  const getCurrentSortLabel = () => {
    const current = sortOptions.find(option => option.id === sortOption);
    return current ? current.label : t("schedules.filters.sortBy") || "Ordenar por";
  };

  const getEmployeeId = (employee) => {
    return employee.employee || Math.random().toString();
  };

  const toggleEmployeeSelection = (employee) => {
    const employeeId = getEmployeeId(employee);

    if (selectedEmployees.size === allEmployees.length && !hasUserInteracted) {
      setHasUserInteracted(true);
      const newSelected = new Set();
      newSelected.add(employeeId);
      setSelectedEmployees(newSelected);
      return;
    }

    const newSelected = new Set(selectedEmployees);
    if (newSelected.has(employeeId)) {
      newSelected.delete(employeeId);
    } else {
      newSelected.add(employeeId);
    }
    setSelectedEmployees(newSelected);

    if (!hasUserInteracted) {
      setHasUserInteracted(true);
    }
  };

  const selectAllVisible = () => {
    const newSelected = new Set();
    searchFilteredEmployees.forEach(employee => {
      newSelected.add(getEmployeeId(employee));
    });
    setSelectedEmployees(newSelected);
    setHasUserInteracted(true);
  };

  const deselectAllVisible = () => {
    const newSelected = new Set(selectedEmployees);
    searchFilteredEmployees.forEach(employee => {
      newSelected.delete(getEmployeeId(employee));
    });
    setSelectedEmployees(newSelected);
    setHasUserInteracted(true);
  };

  const areAllVisibleSelected = () => {
    if (!hasUserInteracted) return false;

    return searchFilteredEmployees.every(employee =>
      selectedEmployees.has(getEmployeeId(employee))
    );
  };

  const handleSearchFocus = () => {
    setShowSearchSuggestions(true);
    setHasUserInteracted(true);
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setShowSearchSuggestions(true);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSearchSuggestions(false);
      }
    };

    if (showSearchSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSearchSuggestions]);

  return (
    <div className="bg-white border-b border-gray-200 p-4 space-y-4">
      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder={t("schedules.filters.searchPlaceholder") || "Pesquisar funcionários..."}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={handleSearchFocus}
          />
          {searchQuery.length > 0 && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <button
                onClick={clearSearch}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>

        {showSearchSuggestions && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <button
                  onClick={areAllVisibleSelected() ? deselectAllVisible : selectAllVisible}
                  className="flex items-center space-x-2 text-sm font-medium text-blue-600 hover:text-blue-800"
                >
                  <div className={`w-4 h-4 border-2 rounded ${areAllVisibleSelected()
                    ? 'bg-blue-600 border-blue-600'
                    : 'border-gray-300'
                    } flex items-center justify-center`}>
                    {areAllVisibleSelected() && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <span>
                    {areAllVisibleSelected() ? "Desmarcar Todos" : "Marcar Todos"}
                  </span>
                </button>
              </div>

              <button
                onClick={() => setShowSearchSuggestions(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="max-h-64 overflow-y-auto">
              {searchFilteredEmployees.length > 0 ? (
                searchFilteredEmployees.map((employee, index) => {
                  const employeeId = getEmployeeId(employee);
                  const isSelected = hasUserInteracted ? selectedEmployees.has(employeeId) : false;
                  const employeeName = employee.employee || `Funcionário ${index + 1}`;

                  return (
                    <button
                      key={employeeId}
                      onClick={() => toggleEmployeeSelection(employee)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 border-b border-gray-100 ${isSelected ? 'bg-blue-50' : ''
                        }`}
                    >
                      <div className={`w-4 h-4 border-2 rounded ${isSelected
                        ? 'bg-blue-600 border-blue-600'
                        : 'border-gray-300'
                        } flex items-center justify-center`}>
                        {isSelected && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div className="flex-1 text-left">
                        <div className={`text-sm font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'
                          }`}>
                          {employeeName}
                        </div>
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="px-4 py-8 text-center text-gray-500">
                  <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">Nenhum funcionário encontrado</p>
                </div>
              )}
            </div>

            <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
              <p className="text-xs text-gray-600 text-center">
                {selectedEmployees.size} selecionados de {allEmployees.length} funcionários
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative">
          <button
            onClick={() => setShowSortModal(true)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Filter className="h-4 w-4 mr-2 text-blue-600" />
            <span className="text-blue-600">{getCurrentSortLabel()}</span>
            <ChevronRight className="h-4 w-4 ml-2 text-blue-600" />
          </button>

          {showSortModal && (
            <>
              <div
                className="z-10"
                onClick={() => setShowSortModal(false)}
              />
              <div className="absolute left-0 z-20 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                <div className="py-1">
                  <div className="px-4 py-2 text-sm font-medium text-gray-900 border-b border-gray-200">
                    {t("schedules.filters.sortBy") || "Ordenar por"}
                  </div>
                  {sortOptions.map((option) => {
                    const IconComponent = option.icon;
                    return (
                      <button
                        key={option.id}
                        onClick={() => {
                          setSortOption(option.id);
                          setShowSortModal(false);
                        }}
                        className={`${sortOption === option.id
                          ? 'bg-blue-50 text-blue-900'
                          : 'text-gray-700'
                          } group flex items-center w-full px-4 py-2 text-sm hover:bg-gray-100`}
                      >
                        <IconComponent
                          className={`h-4 w-4 mr-3 ${sortOption === option.id ? 'text-blue-600' : 'text-gray-400'
                            }`}
                        />
                        {option.label}
                        {sortOption === option.id && (
                          <Check className="h-4 w-4 ml-auto text-blue-600" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="text-sm text-gray-500">
          {filteredCount} {t("schedules.filters.of") || "de"} {employeeCount} {t("schedules.filters.employees") || "funcionários"}
          {selectedEmployees.size > 0 && (
            <span className="ml-2 text-blue-600 font-medium">
              ({selectedEmployees.size} {t("generate.selected")})
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const RosterResultPage = () => {
  const location = useLocation();
  const isExampleMode = location.state?.isExample;
  const scheduleData = JSON.parse(sessionStorage.getItem('rosterResult') || '{}');
  const [weekIndex, setWeekIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const [scenarioId, setScenarioId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('name_asc');
  const [selectedEmployees, setSelectedEmployees] = useState(new Set());
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const getEmployeeId = React.useCallback((employee) => {
    return employee.employee || Math.random().toString();
  }, []);

  const getFirstName = (employee) => {
    if (!employee) return '';
    const fullName = employee.employee || '';
    return fullName.split(' ')[0] || '';
  };

  const getLastName = (employee) => {
    if (!employee) return '';
    const fullName = employee.employee || '';
    const parts = fullName.split(' ');
    return parts.length > 1 ? parts[parts.length - 1] : '';
  };

  const extractEmployeeNumberForSort = React.useCallback((employee) => {
    if (!employee) return 0;

    const name = employee.employee || '';

    const startMatch = name.match(/^(\d+)/);
    if (startMatch) {
      return parseInt(startMatch[1], 10);
    }

    const anyMatch = name.match(/(\d+)/);
    if (anyMatch) {
      return parseInt(anyMatch[1], 10);
    }

    if (scheduleData?.schedule) {
      const index = scheduleData.schedule.findIndex(emp =>
        emp.employee === name
      );
      return index >= 0 ? index + 1 : 0;
    }

    return 0;
  }, [scheduleData]);

  const filteredAndSortedEmployees = useMemo(() => {
    if (!scheduleData?.schedule) return [];

    let employees = [...scheduleData.schedule];

    if (!hasUserInteracted ||
      (hasUserInteracted && selectedEmployees.size === scheduleData.schedule.length)) {
      employees.sort((a, b) => {
        switch (sortOption) {
          case 'name_asc':
            return getFirstName(a).localeCompare(getFirstName(b));
          case 'name_desc':
            return getFirstName(b).localeCompare(getFirstName(a));
          case 'lastname_asc':
            return getLastName(a).localeCompare(getLastName(b));
          case 'lastname_desc':
            return getLastName(b).localeCompare(getLastName(a));
          case 'employee_asc':
            return extractEmployeeNumberForSort(a) - extractEmployeeNumberForSort(b);
          case 'employee_desc':
            return extractEmployeeNumberForSort(b) - extractEmployeeNumberForSort(a);
          default:
            return 0;
        }
      });
      return employees;
    }

    if (selectedEmployees.size > 0) {
      employees = employees.filter(employee => {
        const employeeId = getEmployeeId(employee);
        return selectedEmployees.has(employeeId);
      });
    } else {
      return [];
    }

    employees.sort((a, b) => {
      switch (sortOption) {
        case 'name_asc':
          return getFirstName(a).localeCompare(getFirstName(b));
        case 'name_desc':
          return getFirstName(b).localeCompare(getFirstName(a));
        case 'lastname_asc':
          return getLastName(a).localeCompare(getLastName(b));
        case 'lastname_desc':
          return getLastName(b).localeCompare(getLastName(a));
        case 'employee_asc':
          return extractEmployeeNumberForSort(a) - extractEmployeeNumberForSort(b);
        case 'employee_desc':
          return extractEmployeeNumberForSort(b) - extractEmployeeNumberForSort(a);
        default:
          return 0;
      }
    });

    return employees;
  }, [scheduleData, sortOption, selectedEmployees, hasUserInteracted, getFirstName, getLastName, extractEmployeeNumberForSort, getEmployeeId]);

  useEffect(() => {
    if (scheduleData?.schedule && selectedEmployees.size === 0 && !hasUserInteracted) {
      const allEmployeeIds = new Set();
      scheduleData.schedule.forEach(employee => {
        const employeeId = getEmployeeId(employee);
        allEmployeeIds.add(employeeId);
      });
      setSelectedEmployees(allEmployeeIds);
    }
  }, [scheduleData, hasUserInteracted, selectedEmployees.size, getEmployeeId]);

  useEffect(() => {
    const getScenarioId = () => {

      if (location.state?.scenarioId) {
        return location.state.scenarioId;
      }

      const currentScenario = JSON.parse(sessionStorage.getItem('currentScenario'));
      if (currentScenario?.id) {
        return currentScenario.id;
      }

      const storedScenarioId = JSON.parse(sessionStorage.getItem('scenarioId'));
      if (storedScenarioId?.scenarioId) {
        return storedScenarioId.scenarioId;
      }

      return null;
    };

    const id = getScenarioId();
    setScenarioId(id);
  }, [location]);

  const handleSaveSchedule = async () => {
    if (isSaved) return;

    setIsSaving(true)
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token not found');

      const response = await fetch(`https://www.ia-schedule.com:4139/api/roster/save?scenarioId=${scenarioId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(scheduleData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Erro detalhado:', errorData);
        throw new Error(errorData.message || 'Failed to save schedule');
      }

      setIsSaved(true)
    } catch (error) {
      showAlert({
        title: t("alert.error"),
        description: t("alert.error.saveSchedule") + error.message,
        variant: 'destructive'
      })
    } finally {
      setIsSaving(false);
    }
  }

  const clearAllFilters = () => {
    setSearchQuery('');
    setSortOption('name_asc');
    setShowSearchSuggestions(false);
    setHasUserInteracted(false);
    if (scheduleData?.schedule) {
      const allEmployeeIds = new Set();
      scheduleData.schedule.forEach(employee => {
        const employeeId = getEmployeeId(employee);
        allEmployeeIds.add(employeeId);
      });
      setSelectedEmployees(allEmployeeIds);
    }
  };

  const hasActiveFilters = () => {
    return (
      searchQuery.trim() !== '' ||
      hasUserInteracted ||
      sortOption !== 'name_asc'
    );
  };

  if (!scheduleData.schedule) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Card className="bg-red-50">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold text-red-700 mb-2">No roster data available</h2>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalWeeks = Math.ceil(scheduleData.schedule[0].shifts.length / 7);

  const nextWeek = () => {
    if (weekIndex < totalWeeks - 1) setWeekIndex(weekIndex + 1);
  };

  const previousWeek = () => {
    if (weekIndex > 0) setWeekIndex(weekIndex - 1);
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen -mt-16">
      <div className="max-w-full mx-auto">
        <BackButton onClick={() => window.history.back()} />

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4 mt-4">
          <div className='flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4'>
            <h2 className="text-xl md:text-2xl font-bold">{t("schedules.detail.title")}</h2>

            {hasActiveFilters() && (
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-red-50 text-red-600 border border-red-200 rounded-md hover:bg-red-100"
              >
                <FilterX className="w-3 h-3" />
                <span>{t("schedules.filters.clean")}</span>
              </button>
            )}

            {!isExampleMode && (
              <button
                onClick={handleSaveSchedule}
                disabled={isSaving || isSaved}
                className={`w-full md:w-auto
                  px-3 py-2 md:px-4 md:py-2 rounded-lg flex items-center justify-center gap-1 md:gap-2
                  ${isSaved
                    ? 'bg-green-600 cursor-not-allowed'
                    : isSaving
                      ? 'bg-blue-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }
                  text-white text-sm md:text-base
                `}
              >
                {isSaved ? (
                  <>
                    <Check className='w-4 h-4' />
                    <span className="hidden md:inline">{t("schedules.detail.saved")}</span>
                  </>
                ) : isSaving ? (
                  <>
                    <Save className='w-4 h-4' />
                    <span>{t("schedules.detail.saving")}</span>
                  </>
                ) : (
                  <>
                    <Save className='w-4 h-4' />
                    <span className="hidden md:inline">{t("schedules.detail.save")}</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <Card className="mb-4">
          <SearchAndFilterComponent
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            sortOption={sortOption}
            setSortOption={setSortOption}
            employeeCount={scheduleData.schedule.length}
            filteredCount={filteredAndSortedEmployees.length}
            allEmployees={scheduleData.schedule}
            selectedEmployees={selectedEmployees}
            setSelectedEmployees={setSelectedEmployees}
            showSearchSuggestions={showSearchSuggestions}
            setShowSearchSuggestions={setShowSearchSuggestions}
            hasUserInteracted={hasUserInteracted}
            setHasUserInteracted={setHasUserInteracted}
          />
        </Card>

        <Card className="mb-4">
          <div className="flex flex-col md:flex-row items-end md:items-center gap-2 md:gap-4 w-full md:w-auto p-4">
            <div className="flex items-center gap-2 md:gap-4 w-full justify-between">
              <span className="text-sm text-gray-500 whitespace-nowrap">
                {t("schedules.detail.week")} {weekIndex + 1} {t("schedules.detail.of")} {totalWeeks} | {filteredAndSortedEmployees.length} {t("schedules.filters.employees") || "funcionários"}
              </span>

              <div className="flex gap-1 md:gap-2">
                <button
                  onClick={previousWeek}
                  disabled={weekIndex === 0}
                  className="p-1 md:p-1.5 rounded hover:bg-gray-100 disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4 md:h-5 md:w-5" />
                </button>
                <button
                  onClick={nextWeek}
                  disabled={weekIndex === totalWeeks - 1}
                  className="p-1 md:p-1.5 rounded hover:bg-gray-100 disabled:opacity-50"
                >
                  <ChevronRight className="h-4 w-4 md:h-5 md:w-5" />
                </button>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <CardContent className="p-6">
            {filteredAndSortedEmployees.length > 0 ? (
              <ScheduleTable
                scheduleData={{ ...scheduleData, schedule: filteredAndSortedEmployees }}
                weekIndex={weekIndex}
                translations={t}
              />
            ) : hasUserInteracted ? (
              <div className="text-center py-16">
                <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-600 mb-2">
                  Nenhum funcionário selecionado
                </h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  Use a barra de pesquisa para selecionar funcionários específicos e visualizar seus horários.
                </p>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const formatTime = (time) => {
  if (time === 0) return '00:00';
  if (time === 24) return '24:00';

  const totalHours = Math.floor(time);
  const displayHours = totalHours % 24;

  const minutes = Math.round((time - totalHours) * 60);

  return `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

const formatHoursToTimeString = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return `${hours}:${mins.toString().padStart(2, '0')}h`;
};

const ScheduleTable = ({ scheduleData, weekIndex, translations }) => {
  const weekDays = [
    translations('schedules.table.monday'),
    translations('schedules.table.tuesday'),
    translations('schedules.table.wednesday'),
    translations('schedules.table.thursday'),
    translations('schedules.table.friday'),
    translations('schedules.table.saturday'),
    translations('schedules.table.sunday')
  ];

  const getShiftForDay = (shifts, dayIndex) => {
    return shifts[weekIndex * 7 + dayIndex];
  };

  const employeeStats = useMemo(() => {
    if (!scheduleData?.schedule) return [];

    return scheduleData.schedule.map(employee => {
      let totalMinutes = 0;
      let weekMinutes = 0;
      let totalShifts = 0;
      let weekShifts = 0;
      let workingShifts = 0;
      let weekWorkingShifts = 0;

      employee.shifts.forEach((shift, shiftIndex) => {
        if (!shift) return;

        const isCurrentWeek = Math.floor(shiftIndex / 7) === weekIndex;
        const isNonWorking = ['Off', 'Vacation', 'Absence', 'Holiday', 'Comp'].includes(shift.type);

        if (!isNonWorking) {
          const shiftDuration = (shift.endTime - shift.startTime) * 60;
          totalMinutes += shiftDuration;
          workingShifts++;

          if (isCurrentWeek) {
            weekMinutes += shiftDuration;
            weekWorkingShifts++;
          }
        }

        totalShifts++;
        if (isCurrentWeek) {
          weekShifts++;
        }
      });

      return {
        name: employee.employee,
        totalMinutes,
        weekMinutes,
        totalShifts,
        weekShifts,
        workingShifts,
        weekWorkingShifts
      };
    });
  }, [scheduleData, weekIndex]);

  const decimalToTimeString = (decimalTime) => {
    if (decimalTime === 0) return '00:00';
    if (decimalTime === 24) return '24:00';

    const hours = Math.floor(decimalTime);
    const minutes = Math.round((decimalTime - hours) * 60);

    if (minutes === 60) {
      return `${hours + 1}:00`;
    }

    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const formatShiftCode = (shiftCode) => {
    if (!shiftCode) return '';

    let formatted = shiftCode.replace(/\(([A-Z]+)(\d+)[.,](\d+)\)\s+(\d+)[.,](\d+)-(\d+)/g,
      (match, prefix, prefixHours, prefixMins, startHours, startMins, endHours) => {
        const prefixTime = decimalToTimeString(parseFloat(`${prefixHours}.${prefixMins}`));
        const startTime = decimalToTimeString(parseFloat(`${startHours}.${startMins}`));
        return `(${prefix}${prefixTime}) ${startTime}-${endHours}:00`;
      }
    );

    formatted = formatted.replace(/\(([A-Z]+)(\d+)[.,](\d+)\)\s+(\d+)-(\d+)[.,](\d+)/g,
      (match, prefix, prefixHours, prefixMins, startHours, endHours, endMins) => {
        const prefixTime = decimalToTimeString(parseFloat(`${prefixHours}.${prefixMins}`));
        const endTime = decimalToTimeString(parseFloat(`${endHours}.${endMins}`));
        return `(${prefix}${prefixTime}) ${startHours}-${endTime}`;
      }
    );

    formatted = formatted.replace(/\(([A-Z]+)(\d+)[.,](\d+)\)/g,
      (match, prefix, hours, mins) => {
        return `(${prefix}${decimalToTimeString(parseFloat(`${hours}.${mins}`))})`;
      }
    );

    formatted = formatted.replace(/(\d+)-(\d+)[.,](\d+)/g,
      (match, start, hours, mins) => {
        return `${start}-${decimalToTimeString(parseFloat(`${hours}.${mins}`))}`;
      }
    );

    formatted = formatted.replace(/(\d+)[.,](\d+)/g,
      (match, hours, mins) => {
        return decimalToTimeString(parseFloat(`${hours}.${mins}`));
      }
    );

    return formatted;
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="md:w-48 px-2 py-2 md:px-3 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {translations("schedules.detail.employee")}
            </th>
            {weekDays.map((day, index) => (
              <th key={day} className="px-2 py-2 md:px-3 md:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                <span className="hidden md:inline">{day}</span>
                <span className="md:hidden">{day.substring(0, 3)}</span>
              </th>
            ))}
            <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-indigo-50">
              {translations("employeeHours.weekHours") || "Week Hours"}
            </th>
            <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-indigo-50">
              {translations("employeeHours.totalHours") || "Total Hours"}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {scheduleData.schedule.map((employee, empIndex) => (
            <tr key={empIndex} className="hover:bg-gray-50">
              <td className="px-2 py-1 md:px-3 md:py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                {employee.employee}
              </td>
              {weekDays.map((_, dayIndex) => {
                const shift = getShiftForDay(employee.shifts, dayIndex);
                if (!shift) return <td key={dayIndex} className="px-3 py-2 text-center"></td>;

                let bgColor = 'bg-white';
                const isNonWorking = ['Off', 'Vacation', 'Absence', 'Holiday', 'Comp'].includes(shift.type);
                if (isNonWorking) bgColor = 'bg-gray-100';

                const formattedShiftCode = formatShiftCode(shift.shift);

                return (
                  <td key={dayIndex} className={`px-2 py-1 md:px-3 md:py-2 ${bgColor}`}>
                    <div className="text-sm">
                      <div className="font-medium text-xs md:text-sm">
                        {formattedShiftCode}
                      </div>
                      {!isNonWorking && (
                        <div className="text-gray-500 text-xs">
                          {formatTime(shift.startTime)}-{formatTime(shift.endTime)}
                        </div>
                      )}
                    </div>
                  </td>
                );
              })}

              <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-center bg-indigo-50">
                <span className="text-indigo-600">
                  {formatHoursToTimeString(employeeStats[empIndex]?.weekMinutes || 0)}
                </span>
              </td>

              <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-center bg-indigo-50">
                <span className="text-indigo-600">
                  {formatHoursToTimeString(employeeStats[empIndex]?.totalMinutes || 0)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RosterResultPage;